import React, { useEffect, useRef, useState } from 'react';
import { GraphMember, useFamilyGraph } from '@/contexts/FamilyGraphContext';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/sonner';
import {
  X,
  Camera,
  Loader2,
  User,
  Calendar,
  BookOpen,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { formatDobDigits } from './BirthdayInput';

interface Props {
  open: boolean;
  member: GraphMember;
  ownerUserId: string | null;
  /** Email username fallback for the placeholder name. */
  fallbackName: string;
  canUploadProfilePhoto?: boolean;
  remainingPhotoCount?: number | null;
  onClose: () => void;
}

const MAX_AVATAR_BYTES = 8 * 1024 * 1024; // 8 MB

/** MM/DD/YYYY ↔ YYYY-MM-DD helpers. */
const isoToMDY = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  return `${m[2]}/${m[3]}/${m[1]}`;
};

const mdyToIso = (mdy: string): { iso: string | null; error: string | null } => {
  const trimmed = mdy.trim();
  if (!trimmed) return { iso: null, error: null };
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (!m) {
    return { iso: null, error: 'Use MM/DD/YYYY (e.g. 04/26/1990).' };
  }
  const month = parseInt(m[1], 10);
  const day = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12) return { iso: null, error: 'Month must be 01–12.' };
  if (day < 1 || day > 31) return { iso: null, error: 'Day must be 01–31.' };
  if (year < 1800 || year > new Date().getUTCFullYear()) {
    return { iso: null, error: 'Year must be 1800–this year.' };
  }
  // Quick validity check via Date
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return { iso: null, error: 'That date doesn\u2019t exist.' };
  }
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return { iso: `${year}-${mm}-${dd}`, error: null };
};



const EditProfileModal: React.FC<Props> = ({
  open,
  member,
  ownerUserId,
  fallbackName,
  canUploadProfilePhoto = true,
  remainingPhotoCount = null,
  onClose,
}) => {
  const { updateMember } = useFamilyGraph();

  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [treeNamePreference, setTreeNamePreference] =
    useState<'real-name' | 'nickname'>('real-name');
  const [birthdayMDY, setBirthdayMDY] = useState('');
  const [bio, setBio] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const avatarInputRef = useRef<HTMLInputElement | null>(null);

  // Load member values when modal opens / member changes.
  useEffect(() => {
    if (!open) return;
    setName(member.name && member.name !== 'Unknown' ? member.name : fallbackName);
    setNickname(member.nickname || '');
    setTreeNamePreference(
      member.treeNamePreference ?? 'real-name',
    );
    setBirthdayMDY(isoToMDY(member.birthDate));
    setBio(member.notes || '');
    setPhotoUrl(member.photoUrl || null);
    setPhotoError(null);
    setErrorMsg(null);
    setSaving(false);
    setPhotoUploading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member.id]);

  if (!open) return null;

  const handlePickAvatar = () => {
    if (saving || photoUploading) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarFile = async (file: File) => {
    setPhotoError(null);

    if (
      !canUploadProfilePhoto &&
      !member.photoUrl
    ) {
      setPhotoError(
        remainingPhotoCount === 0
          ? 'Your five-photo Family Contributor limit has been reached.'
          : 'You do not have authority to change this profile photo.',
      );
      return;
    }
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setPhotoError('Image is too large — max 8 MB.');
      return;
    }
    setPhotoUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
      const safeUser = ownerUserId ? ownerUserId.replace(/[^a-zA-Z0-9-]/g, '') : 'anon';
      const path = `avatars/${safeUser}/${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('family-media')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('family-media').getPublicUrl(path);
      setPhotoUrl(data.publicUrl);
    } catch (err: any) {
      console.error('[EditProfile] avatar upload failed', err);
      setPhotoError(err?.message || 'Could not upload photo.');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSave = async () => {
    setErrorMsg(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMsg('Please enter a name.');
      return;
    }
    const { iso, error: dateErr } = mdyToIso(birthdayMDY);
    if (dateErr) {
      setErrorMsg(dateErr);
      return;
    }

    setSaving(true);
    try {
      const updates: Partial<GraphMember> = {
        name: trimmedName,
        nickname: nickname.trim() || null,
        treeNamePreference:
          treeNamePreference === 'nickname' &&
          nickname.trim()
            ? 'nickname'
            : 'real-name',
        notes: bio.trim() || null,
        photoUrl: photoUrl || null,
        birthDate: iso,
      };
      // Also keep the legacy month/day/year columns in sync for the
      // birthdays-coming-up reminder code.
      if (iso) {
        const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
        updates.birthYear = y;
        updates.birthMonth = m;
        updates.birthDay = d;
      } else {
        updates.birthYear = null;
        updates.birthMonth = null;
        updates.birthDay = null;
      }
      await updateMember(member.id, updates);
      toast.success('Profile updated');
      onClose();
    } catch (err: any) {
      console.error('[EditProfile] save failed', err);
      setErrorMsg(err?.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const initials = (name || fallbackName)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      onClick={saving || photoUploading ? undefined : onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 text-white px-5 py-4 flex items-center gap-3">
          <Sparkles className="w-5 h-5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-serif font-bold text-lg leading-tight">Edit Profile</div>
            <div className="text-[11px] opacity-90 truncate">
              Update your name, nickname, birthdate, photo & bio.
            </div>
          </div>
          <button
            type="button"
            onClick={saving || photoUploading ? undefined : onClose}
            disabled={saving || photoUploading}
            className="text-white/85 hover:text-white p-1 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Avatar */}
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={handlePickAvatar}
              disabled={saving || photoUploading}
              className="relative w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-fuchsia-500 to-pink-500 border-4 border-white shadow-lg flex items-center justify-center disabled:opacity-50"
            >
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-2xl">{initials}</span>
              )}
              <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] font-bold py-1 flex items-center justify-center gap-1">
                {photoUploading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" /> Uploading
                  </>
                ) : (
                  <>
                    <Camera className="w-3 h-3" /> Change
                  </>
                )}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleAvatarFile(f);
                e.target.value = '';
              }}
            />
            {photoError && (
              <div className="mt-2 text-xs text-rose-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {photoError}
              </div>
            )}
            <p className="mt-2 text-[11px] text-slate-500 text-center">
              Profile photo · separate from timeline photos.
            </p>
          </div>

          {/* Full name */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5 mb-1">
              <User className="w-3.5 h-3.5" /> Full name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              placeholder="Your full name"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-100 outline-none disabled:bg-slate-50"
            />
          </div>

          {/* Nickname */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5 mb-1">
              <Sparkles className="w-3.5 h-3.5" /> Nickname
              <span className="text-slate-400 font-normal normal-case tracking-normal">
                (optional)
              </span>
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={saving}
              placeholder="What family calls you"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-100 outline-none disabled:bg-slate-50"
            />
          </div>

          {/* Tree-card display preference */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5 mb-1">
              <Sparkles className="w-3.5 h-3.5" />
              Name shown on the family tree
            </label>
            <select
              value={treeNamePreference}
              onChange={(e) =>
                setTreeNamePreference(
                  e.target.value as
                    | 'real-name'
                    | 'nickname',
                )
              }
              disabled={
                saving ||
                !nickname.trim()
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-100 outline-none disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="real-name">
                Full name
              </option>
              <option value="nickname">
                Nickname
              </option>
            </select>
            <p className="text-[10px] text-slate-500 mt-1">
              Your full name remains the required profile identity.
              Nickname changes only the compact Tree card.
            </p>
          </div>

          {/* Birthdate */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5 mb-1">
              <Calendar className="w-3.5 h-3.5" /> Date of birth
              <span className="text-slate-400 font-normal normal-case tracking-normal">
                MM/DD/YYYY
              </span>
            </label>
            <input
              type="text"
              value={birthdayMDY}
              onChange={(e) => {
                // Use the same `formatDobDigits` helper used by
                // BirthdayInput, so this field auto-inserts slashes
                // after the 2nd and 4th digits exactly the same way.
                // The user types digits only; backspace works because
                // the formatter never injects a slash unless the digit
                // before it is actually present in the buffer.
                setBirthdayMDY(formatDobDigits(e.target.value));
              }}
              disabled={saving}
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              autoComplete="bday"
              maxLength={10}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono tracking-wider focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-100 outline-none disabled:bg-slate-50"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Slashes appear automatically as you type.
            </p>

          </div>

          {/* Bio / history */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5 mb-1">
              <BookOpen className="w-3.5 h-3.5" /> Bio & history
              <span className="text-slate-400 font-normal normal-case tracking-normal">
                (optional)
              </span>
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              disabled={saving}
              rows={4}
              placeholder="A short story about who you are, where you're from, what you love…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-100 outline-none disabled:bg-slate-50"
            />
          </div>

          {errorMsg && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {errorMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={saving || photoUploading ? undefined : onClose}
            disabled={saving || photoUploading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || photoUploading}
            className="px-5 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:opacity-95 text-white disabled:opacity-40 inline-flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>Save changes</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
